import type { WebsiteLink } from "../components/WebsiteGrid";
import { continents } from "./constants";
import { 
  getAggregatedContinentCount, 
  getAggregatedCountryCount, 
  getAggregatedCityCount,
  matchesFilters as defaultMatchesFilters
} from "./filterHelpers";

// Lazy-initialized location hierarchy with memoization
// This avoids rebuilding on every hot module reload during development
let locationHierarchyCache: {
  [continent: string]: { [country: string]: Set<string> };
} | null = null;

const buildLocationHierarchy = (websiteLinks: WebsiteLink[]) => {
  const hierarchy: {
    [continent: string]: { [country: string]: Set<string> };
  } = {};

  websiteLinks.forEach((link) => {
    if (link.continent) {
      if (!hierarchy[link.continent]) {
        hierarchy[link.continent] = {};
      }

      if (link.countries && link.countries.length > 0) {
        link.countries.forEach((country) => {
          if (!hierarchy[link.continent!][country]) {
            hierarchy[link.continent!][country] = new Set<string>();
          }

          if (link.cities && link.cities.length > 0) {
            link.cities.forEach((city) => {
              hierarchy[link.continent!][country].add(city);
            });
          }
        });
      }
    }
  });

  return hierarchy;
};

// Lazy getter - only builds hierarchy when first accessed
export const getLocationHierarchy = (websiteLinks: WebsiteLink[]) => {
  if (locationHierarchyCache === null) {
    locationHierarchyCache = buildLocationHierarchy(websiteLinks);
  }
  return locationHierarchyCache;
};

// === LOCATION AVAILABILITY FUNCTIONS ===

export const getAvailableContinents = (
  websiteLinks: WebsiteLink[],
  categoryFilter?: string[],
  tagsFilter?: string[],
  matchesFilters?: (link: WebsiteLink, categoryFilter?: string[], tagsFilter?: string[]) => boolean
) => {
  const continentsWithMaps = new Set<string>();
  websiteLinks.forEach((link) => {
    if (matchesFilters && !matchesFilters(link, categoryFilter, tagsFilter)) {
      return;
    }
    if (link.continent) {
      continentsWithMaps.add(link.continent);
    }
  });
  // Return the sorted list of continents
  return Array.from(continentsWithMaps).sort();
};

export const getAvailableCountries = (
  websiteLinks: WebsiteLink[],
  continent: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
  matchesFilters?: (link: WebsiteLink, categoryFilter?: string[], tagsFilter?: string[]) => boolean
) => {
  const countriesWithMaps = new Set<string>();
  websiteLinks.forEach((link) => {
    if (matchesFilters && !matchesFilters(link, categoryFilter, tagsFilter)) {
      return;
    }
    if (link.continent === continent && link.countries) {
      link.countries.forEach((country) => countriesWithMaps.add(country));
    }
  });
  // Use the dynamically built hierarchy instead of manual list
  return getCountriesForContinent(websiteLinks, continent).filter((c) =>
    countriesWithMaps.has(c),
  );
};

export const getAvailableCities = (
  websiteLinks: WebsiteLink[],
  country: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
  matchesFilters?: (link: WebsiteLink, categoryFilter?: string[], tagsFilter?: string[]) => boolean
) => {
  // Use O(1) reverse lookup map instead of O(n) forEach loop
  const countryContinent = getCountryToContinentMap(websiteLinks).get(country) || "";
  
  const citiesWithMaps = new Set<string>();
  websiteLinks.forEach((link) => {
    if (matchesFilters && !matchesFilters(link, categoryFilter, tagsFilter)) {
      return;
    }
    if (link.countries?.includes(country) && link.cities) {
      link.cities.forEach((city) => citiesWithMaps.add(city));
    }
  });
  
  // Use the dynamically built hierarchy instead of manual list
  return getCitiesForCountry(websiteLinks, countryContinent, country).filter(
    (c) => citiesWithMaps.has(c),
  );
};

// === REVERSE LOOKUP MAPS ===
// Build once, use many times for O(1) lookups

let countryToContinentMapCache: Map<string, string> | null = null;

const getCountryToContinentMap = (websiteLinks: WebsiteLink[]): Map<string, string> => {
  if (countryToContinentMapCache === null) {
    countryToContinentMapCache = new Map<string, string>();
    const hierarchy = getLocationHierarchy(websiteLinks);
    
    Object.keys(hierarchy).forEach((continent) => {
      Object.keys(hierarchy[continent]).forEach((country) => {
        // Store first occurrence only (handles edge case of same country in multiple continents)
        if (!countryToContinentMapCache!.has(country)) {
          countryToContinentMapCache!.set(country, continent);
        }
      });
    });
  }
  return countryToContinentMapCache;
};

const getCountriesForContinent = (
  websiteLinks: WebsiteLink[],
  continent: string,
): string[] => {
  const countries = getLocationHierarchy(websiteLinks)[continent];
  return countries ? Object.keys(countries).sort() : [];
};

const getCitiesForCountry = (
  websiteLinks: WebsiteLink[],
  continent: string,
  country: string,
): string[] => {
  const cities = getLocationHierarchy(websiteLinks)[continent]?.[country];
  return cities ? Array.from(cities).sort() : [];
};

// === PARENT LOCATION HELPERS (for Search tab parent inclusion) ===

/**
 * Get parent locations for a given location
 * @param location - City, Country, or Continent name
 * @param websiteLinks - All website links for hierarchy lookup
 * @returns Array of parent location names, ordered from immediate parent to World
 */
export const getParentLocations = (
  location: string,
  websiteLinks: WebsiteLink[]
): string[] => {
  const parents: string[] = [];
  const hierarchy = getLocationHierarchy(websiteLinks);
  
  // Check if it's a continent
  if (continents.includes(location)) {
    parents.push('World');
    return parents;
  }
  
  // Check if it's a country
  const countryToContinent = getCountryToContinentMap(websiteLinks);
  const continent = countryToContinent.get(location);
  if (continent) {
    parents.push(continent, 'World');
    return parents;
  }
  
  // Must be a city - find its country and continent
  for (const [cont, countries] of Object.entries(hierarchy)) {
    for (const [country, cities] of Object.entries(countries)) {
      if (cities.has(location)) {
        parents.push(country, cont, 'World');
        return parents;
      }
    }
  }
  
  return parents;
};

/**
 * Check if a map matches any of the parent locations
 * @param map - Website link to check
 * @param parentLocations - Array of parent location names
 * @returns True if map matches any parent location
 */
export const matchesParentLocation = (
  map: WebsiteLink,
  parentLocations: string[]
): boolean => {
  for (const parent of parentLocations) {
    if (parent === 'World' && map.world) return true;
    if (map.continent === parent) return true;
    if (map.countries?.includes(parent)) return true;
    if (map.cities?.includes(parent)) return true;
  }
  return false;
};

// === LOCATION TAB VIEW LEVEL CALCULATION ===

/**
 * Calculate the appropriate view level for LocationTab based on current filters and selections
 * This ensures users see the deepest available level that has maps with current filters
 * 
 * @param websiteLinks - All website links
 * @param selectedContinents - Currently selected continents
 * @param selectedCountries - Currently selected countries
 * @param selectedCities - Currently selected cities
 * @param selectedCategories - Currently selected category filters
 * @param selectedSearchTags - Currently selected search tag filters
 * @returns The appropriate view level: 'continents' | 'countries' | 'cities'
 */
export const calculateLocationTabViewLevel = (
  websiteLinks: WebsiteLink[],
  selectedContinents: string[],
  selectedCountries: string[],
  selectedCities: string[],
  selectedCategories: string[],
  selectedSearchTags: string[]
): 'continents' | 'countries' | 'cities' => {
  const hasFilters = selectedCategories.length > 0 || selectedSearchTags.length > 0;
  
  if (hasFilters) {
    // When filters are active, check if selected location has maps
    if (selectedCities.length > 0) {
      const city = selectedCities[0];
      const cityCount = getAggregatedCityCount(websiteLinks, city, selectedCategories, selectedSearchTags);
      if (cityCount > 0) return 'cities';
      
      // City has no maps with filters - check parent country
      const mapWithCity = websiteLinks.find(link => link.cities?.includes(city));
      const country = mapWithCity?.countries?.[0];
      
      if (country) {
        const countryCount = getAggregatedCountryCount(websiteLinks, country, selectedCategories, selectedSearchTags);
        if (countryCount > 0) return 'countries';
        
        // Country has no maps - check grandparent continent
        const continent = mapWithCity?.continent;
        if (continent) {
          const continentCount = getAggregatedContinentCount(websiteLinks, continent, selectedCategories, selectedSearchTags);
          if (continentCount > 0) return 'countries'; // Show countries level to let user see available countries
        }
      }
      
      // No maps in hierarchy - show continents
      return 'continents';
    } else if (selectedCountries.length > 0) {
      const country = selectedCountries[0];
      const availableCities = getAvailableCities(websiteLinks, country, selectedCategories, selectedSearchTags, defaultMatchesFilters);
      if (availableCities.length > 0) {
        return 'cities';
      }
      // Otherwise check if country itself has maps
      const countryCount = getAggregatedCountryCount(websiteLinks, country, selectedCategories, selectedSearchTags);
      if (countryCount > 0) return 'countries';
      
      // Country has no maps - check grandparent continent (matches selectedCities pattern)
      const mapWithCountry = websiteLinks.find(link => link.countries?.includes(country));
      const continent = mapWithCountry?.continent;
      if (continent) {
        const continentCount = getAggregatedContinentCount(websiteLinks, continent, selectedCategories, selectedSearchTags);
        if (continentCount > 0) return 'countries'; // Show countries level to let user see available countries
      }
      
      // No maps in hierarchy - show continents
      return 'continents';
    } else if (selectedContinents.length > 0) {
      const continent = selectedContinents[0];
      // Check if continent has countries with maps
      const availableCountries = getAvailableCountries(websiteLinks, continent, selectedCategories, selectedSearchTags, defaultMatchesFilters);
      if (availableCountries.length > 0) {
        return 'countries';
      }
      // Otherwise stay at continents level
      return 'continents';
    } else {
      // No location selected, show continents
      return 'continents';
    }
  } else {
    // No filters active - use existing hierarchical logic
    if (selectedCities.length > 0) {
      return 'cities';
    } else if (selectedCountries.length > 0) {
      const country = selectedCountries[0];
      const availableCities = getAvailableCities(websiteLinks, country, selectedCategories, selectedSearchTags, defaultMatchesFilters);
      return availableCities.length > 0 ? 'cities' : 'countries';
    } else if (selectedContinents.length > 0) {
      const continent = selectedContinents[0];
      const availableCountries = getAvailableCountries(websiteLinks, continent, selectedCategories, selectedSearchTags, defaultMatchesFilters);
      return availableCountries.length > 0 ? 'countries' : 'continents';
    } else {
      return 'continents';
    }
  }
};